import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Phone,
  Search,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Building2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhoneLookupPage = () => {
  const { getAuthHeader } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchNumbers, setBatchNumbers] = useState("");
  const [batchResults, setBatchResults] = useState([]);

  const handleLookup = async () => {
    if (!phoneNumber.trim()) {
      toast.error("Please enter a phone number");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/phone-lookup`, 
        { phone_number: phoneNumber, country_code: countryCode },
        { headers: getAuthHeader() }
      );
      setResult(response.data);
      if (!response.data.is_valid) {
        toast.warning("Number may not be valid");
      }
    } catch (error) {
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBatchLookup = async () => {
    if (!batchNumbers.trim()) {
      toast.error("Please enter phone numbers");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/phone-lookup/batch?numbers=${encodeURIComponent(batchNumbers)}&country_code=${countryCode}`,
        { headers: getAuthHeader() }
      );
      setBatchResults(response.data);
      toast.success(`Looked up ${response.data.length} numbers`);
    } catch (error) {
      toast.error("Batch lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const getLineTypeBadge = (type) => {
    const colors = {
      mobile: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      landline: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      voip: "bg-purple-500/10 text-purple-500 border-purple-500/20",
      toll_free: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      unknown: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"
    };
    return colors[type] || colors.unknown;
  };

  return (
    <div className="space-y-6" data-testid="phone-lookup-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
          <Phone className="w-7 h-7 text-primary" />
          Phone Lookup
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Identify phone number details - carrier, type, location
        </p>
      </div>

      {/* Info */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <p className="text-sm">
            <strong>Free lookup:</strong> Uses built-in phone number database to identify carrier, line type 
            (mobile/landline/VoIP), country, and timezone. No external paid API required.
          </p>
        </CardContent>
      </Card>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={!batchMode ? "default" : "outline"}
          size="sm"
          onClick={() => setBatchMode(false)}
        >
          Single Lookup
        </Button>
        <Button
          variant={batchMode ? "default" : "outline"}
          size="sm"
          onClick={() => setBatchMode(true)}
        >
          Batch Lookup
        </Button>
      </div>

      {!batchMode ? (
        /* Single Lookup */
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Enter Phone Number</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="e.g., +1 555-123-4567 or 5551234567"
                  className="bg-background font-mono"
                  data-testid="phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States (+1)</SelectItem>
                    <SelectItem value="CA">Canada (+1)</SelectItem>
                    <SelectItem value="GB">United Kingdom (+44)</SelectItem>
                    <SelectItem value="AU">Australia (+61)</SelectItem>
                    <SelectItem value="DE">Germany (+49)</SelectItem>
                    <SelectItem value="FR">France (+33)</SelectItem>
                    <SelectItem value="IN">India (+91)</SelectItem>
                    <SelectItem value="JP">Japan (+81)</SelectItem>
                    <SelectItem value="BR">Brazil (+55)</SelectItem>
                    <SelectItem value="MX">Mexico (+52)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleLookup} disabled={loading} className="w-full gap-2" data-testid="lookup-btn">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Lookup Number
              </Button>
            </CardContent>
          </Card>

          {/* Result */}
          <Card className="bg-card/50 border-border" data-testid="lookup-result">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Results</CardTitle>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-4">
                  {/* Validity */}
                  <div className="flex items-center gap-3">
                    {result.is_valid ? (
                      <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Valid Number
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/10 text-red-500 border-red-500/20 gap-1">
                        <XCircle className="w-3 h-3" />
                        Invalid
                      </Badge>
                    )}
                    {result.line_type && (
                      <Badge className={getLineTypeBadge(result.line_type)}>
                        {result.line_type}
                      </Badge>
                    )}
                  </div>

                  {/* Formatted Numbers */}
                  {result.formatted_international && (
                    <div className="p-3 bg-background rounded border border-border">
                      <p className="text-2xl font-mono text-primary">{result.formatted_international}</p>
                      <p className="text-sm text-muted-foreground mt-1">{result.formatted_national}</p>
                    </div>
                  )}

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    {result.country && (
                      <div className="p-3 bg-background rounded border border-border">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <MapPin className="w-3 h-3" />
                          <span className="text-xs uppercase">Location</span>
                        </div>
                        <p className="font-medium">{result.country}</p>
                        <p className="text-xs text-muted-foreground">{result.country_code}</p>
                      </div>
                    )}
                    {result.carrier_name && (
                      <div className="p-3 bg-background rounded border border-border">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Building2 className="w-3 h-3" />
                          <span className="text-xs uppercase">Carrier</span>
                        </div>
                        <p className="font-medium">{result.carrier_name}</p>
                      </div>
                    )}
                  </div>

                  {/* Timezones */}
                  {result.timezones && result.timezones.length > 0 && (
                    <div className="p-3 bg-background rounded border border-border">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <Clock className="w-3 h-3" />
                        <span className="text-xs uppercase">Timezone(s)</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {result.timezones.map((tz, i) => (
                          <Badge key={i} variant="outline" className="font-mono text-xs">
                            {tz}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Enter a number to look up</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Batch Lookup */
        <div className="space-y-4">
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Batch Lookup (up to 20 numbers)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Phone Numbers (comma-separated)</Label>
                <Input
                  value={batchNumbers}
                  onChange={(e) => setBatchNumbers(e.target.value)}
                  placeholder="5551234567, 5559876543, 5551112222"
                  className="bg-background font-mono"
                />
              </div>
              <div className="flex gap-4">
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger className="w-[200px] bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States (+1)</SelectItem>
                    <SelectItem value="CA">Canada (+1)</SelectItem>
                    <SelectItem value="GB">United Kingdom (+44)</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleBatchLookup} disabled={loading} className="gap-2">
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Lookup All
                </Button>
              </div>
            </CardContent>
          </Card>

          {batchResults.length > 0 && (
            <Card className="bg-card/50 border-border">
              <CardContent className="p-0">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th className="text-left px-4 py-3">Number</th>
                      <th className="text-left px-4 py-3">Carrier</th>
                      <th className="text-left px-4 py-3">Type</th>
                      <th className="text-left px-4 py-3">Location</th>
                      <th className="text-left px-4 py-3">Valid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchResults.map((r, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 font-mono">{r.formatted_international || r.phone_number}</td>
                        <td className="px-4 py-3">{r.carrier_name || "—"}</td>
                        <td className="px-4 py-3">
                          {r.line_type && <Badge className={getLineTypeBadge(r.line_type)}>{r.line_type}</Badge>}
                        </td>
                        <td className="px-4 py-3">{r.country || "—"}</td>
                        <td className="px-4 py-3">
                          {r.is_valid ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default PhoneLookupPage;
